import {
	getAvailableRounds,
	getCategories,
	getChampionships,
	getZones,
	resolveSeasonYear,
} from '../../services/csvDataService';
import {
	AVAILABLE_YEARS,
	DEFAULT_PLAYER_PAGE_SIZE,
	EQUIPES_URL_PARAM_KEYS,
	JOUEURS_URL_PARAM_KEYS,
	LOCAL_ONLY_URL_PARAM_KEYS,
	MULTI_FILTER_SEPARATOR,
	PLAYER_PAGE_SIZE_OPTIONS,
	EQUIPES_FILTERS_STORAGE_KEY,
	FAVORITES_STORAGE_KEY,
	JOUEURS_FILTERS_STORAGE_KEY,
} from './newResultsConstants';

export function parseMultiFilterValue(rawValue) {
	if (!rawValue) return [];
	if (Array.isArray(rawValue)) {
		return [...new Set(
			rawValue
				.map((value) => String(value ?? '').trim())
				.filter(Boolean),
		)];
	}
	return [...new Set(
		String(rawValue)
			.split(MULTI_FILTER_SEPARATOR)
			.map((value) => value.trim())
			.filter(Boolean),
	)];
}

export function normalizePlayerPageSize(rawValue) {
	const size = Number(rawValue);
	return PLAYER_PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PLAYER_PAGE_SIZE;
}

export function normalizePlayerPage(rawValue) {
	const page = Number(rawValue);
	return Number.isInteger(page) && page > 0 ? page : 1;
}

export function defaultEquipesFilters() {
	return { eqZone: '', cat: '', club: '' };
}

export function equipesFiltersFromStorage(saved) {
	const base = defaultEquipesFilters();
	if (!saved) return base;
	return {
		...base,
		eqZone: saved.eqZone || '',
		cat: saved.cat || '',
		club: saved.club || '',
	};
}

export function equipesFiltersFromLegacyUrl(searchParams) {
	return {
		eqZone: searchParams.get('eqZone') || '',
		cat: searchParams.get('cat') || '',
		club: searchParams.get('club') || '',
	};
}

export function mergeEquipesFiltersWithLegacyUrl(stored, legacy) {
	return {
		...stored,
		eqZone: legacy.eqZone || stored.eqZone,
		cat: legacy.cat || stored.cat,
		club: legacy.club || stored.club,
	};
}

export function hasEquipesUrlParams(searchParams) {
	return EQUIPES_URL_PARAM_KEYS.some((key) => searchParams.has(key));
}

export function applyEquipesParamUpdates(prev, updates) {
	const next = { ...prev };
	if (updates.eqZone !== undefined) {
		next.eqZone = updates.eqZone === null ? '' : String(updates.eqZone);
	}
	if (updates.cat !== undefined) {
		next.cat = updates.cat === null ? '' : String(updates.cat);
	}
	if (updates.club !== undefined) {
		next.club = updates.club === null ? '' : String(updates.club);
	}
	return next;
}

export function stripLocalFilterUrlParams(searchParams) {
	const next = new URLSearchParams(searchParams);
	LOCAL_ONLY_URL_PARAM_KEYS.forEach((key) => next.delete(key));
	return next;
}

export function tabSwitchNavigationResets(tabId) {
	if (tabId === 'equipes' || tabId === 'joueurs' || tabId === 'carriere') {
		return {
			zone: null,
			championship: null,
			category: null,
			round: null,
			view: null,
			stats: null,
		};
	}
	return { stats: null };
}

export function defaultJoueursFilters() {
	return {
		licTypes: [],
		licCats: [],
		pclubs: [],
		minManches: '',
		psize: DEFAULT_PLAYER_PAGE_SIZE,
		page: 1,
		q: '',
	};
}

export function joueursFiltersFromStorage(saved) {
	const base = defaultJoueursFilters();
	if (!saved) return base;
	return {
		...base,
		licTypes: saved.licTypes,
		licCats: saved.licCats,
		pclubs: saved.pclubs,
		minManches: saved.minManches || '',
		psize: saved.psize ?? DEFAULT_PLAYER_PAGE_SIZE,
		q: saved.q || '',
	};
}

export function joueursFiltersFromLegacyUrl(searchParams) {
	return {
		licTypes: parseMultiFilterValue(searchParams.get('licTypes') || searchParams.get('licType')),
		licCats: parseMultiFilterValue(searchParams.get('licCats') || searchParams.get('licCat')),
		pclubs: parseMultiFilterValue(searchParams.get('pclubs') || searchParams.get('pclub')),
		minManches: searchParams.get('minManches') || searchParams.get('minParties') || '',
		psize: normalizePlayerPageSize(searchParams.get('psize')),
		page: normalizePlayerPage(searchParams.get('ppage')),
		q: searchParams.get('q') || '',
	};
}

export function mergeJoueursFiltersWithLegacyUrl(stored, legacy, searchParams) {
	return {
		...stored,
		licTypes: legacy.licTypes.length ? legacy.licTypes : stored.licTypes,
		licCats: legacy.licCats.length ? legacy.licCats : stored.licCats,
		pclubs: legacy.pclubs.length ? legacy.pclubs : stored.pclubs,
		minManches: legacy.minManches || stored.minManches,
		psize: searchParams.has('psize') ? legacy.psize : stored.psize,
		page: searchParams.has('ppage') ? legacy.page : stored.page,
		q: legacy.q || stored.q,
	};
}

export function hasJoueursUrlParams(searchParams) {
	return JOUEURS_URL_PARAM_KEYS.some((key) => searchParams.has(key));
}

export function applyJoueursParamUpdates(prev, updates) {
	const next = { ...prev };
	if (updates.licTypes !== undefined || updates.licType !== undefined) {
		const raw = updates.licTypes ?? updates.licType;
		next.licTypes = raw === null ? [] : parseMultiFilterValue(raw);
	}
	if (updates.licCats !== undefined || updates.licCat !== undefined) {
		const raw = updates.licCats ?? updates.licCat;
		next.licCats = raw === null ? [] : parseMultiFilterValue(raw);
	}
	if (updates.pclubs !== undefined || updates.pclub !== undefined) {
		const raw = updates.pclubs ?? updates.pclub;
		next.pclubs = raw === null ? [] : parseMultiFilterValue(raw);
	}
	if (updates.minManches !== undefined || updates.minParties !== undefined) {
		const raw = updates.minManches ?? updates.minParties;
		next.minManches = raw === null || raw === '' ? '' : String(raw);
	}
	if (updates.psize !== undefined) {
		next.psize = updates.psize === null
			? DEFAULT_PLAYER_PAGE_SIZE
			: normalizePlayerPageSize(updates.psize);
	}
	if (updates.ppage !== undefined) {
		next.page = updates.ppage === null ? 1 : normalizePlayerPage(updates.ppage);
	}
	if (updates.q !== undefined) {
		next.q = updates.q === null ? '' : String(updates.q);
	}
	return next;
}

export function pickUrlParamUpdates(updates) {
	const urlUpdates = {};
	Object.entries(updates).forEach(([key, value]) => {
		if (!LOCAL_ONLY_URL_PARAM_KEYS.includes(key)) {
			urlUpdates[key] = value;
		}
	});
	return urlUpdates;
}

export function readNavigationState(searchParams) {
	const year = resolveSeasonYear(Number(searchParams.get('year')), AVAILABLE_YEARS);
	return {
		year,
		zone: searchParams.get('zone') || '',
		championship: searchParams.get('championship') || '',
		category: searchParams.get('category') || '',
		mainTab: searchParams.get('tab') || 'equipes',
		subView: searchParams.get('view') || 'classement',
		round: searchParams.get('round') || '',
		team: searchParams.get('team') || '',
		playerLicence: searchParams.get('joueur') || '',
		careerStats: searchParams.get('stats') === '1',
		careerFromYear: (() => {
			const raw = searchParams.get('cfrom');
			if (!raw) return null;
			const n = Number(raw);
			return Number.isFinite(n) ? n : null;
		})(),
		careerToYear: (() => {
			const raw = searchParams.get('cto');
			if (!raw) return null;
			const n = Number(raw);
			return Number.isFinite(n) ? n : null;
		})(),
		careerSeniorsOnly: searchParams.get('csenior') === '1',
	};
}

export function buildPageState(searchParams, joueursFilters, equipesFilters) {
	return {
		...readNavigationState(searchParams),
		equipesZone: equipesFilters.eqZone,
		equipesCategory: equipesFilters.cat,
		club: equipesFilters.club,
		playerLicenceTypes: joueursFilters.licTypes,
		playerLicenceCategories: joueursFilters.licCats,
		playerClubs: joueursFilters.pclubs,
		playerMinManches: joueursFilters.minManches,
		playerPageSize: joueursFilters.psize,
		playerPage: joueursFilters.page,
		searchQuery: joueursFilters.q,
	};
}

/** Conserve zone/championnat/catégorie/manche si présents dans les CSV de la saison chargée. */
export function validateCompetitionParams(data, { zone, championship, category, round }) {
	if (!data) {
		return { zone: null, championship: null, category: null, round: null };
	}
	const zones = getZones(data.teams, data.individuals);
	if (!zone || !zones.includes(zone)) {
		return { zone: null, championship: null, category: null, round: null };
	}
	const championships = getChampionships(data.teams, zone, data.individuals);
	if (!championship || !championships.includes(championship)) {
		return { zone, championship: null, category: null, round: null };
	}
	const categories = getCategories(data.teams, zone, championship, data.individuals);
	if (!category || !categories.includes(category)) {
		return { zone, championship, category: null, round: null };
	}
	const filters = { zone, championship, category };
	const rounds = getAvailableRounds(data.teams, filters, data.individuals);
	const validRound = round && rounds.includes(round) ? round : null;
	return { zone, championship, category, round: validRound };
}

export function readFavoriteTeams() {
	if (typeof window === 'undefined') return [];
	try {
		const raw = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed;
	} catch {
		return [];
	}
}

export function readEquipesFilters() {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(EQUIPES_FILTERS_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return null;
		return {
			year: Number(parsed.year) || null,
			eqZone: parsed.eqZone || '',
			cat: parsed.cat || '',
			club: parsed.club || '',
		};
	} catch {
		return null;
	}
}

export function readJoueursFilters() {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(JOUEURS_FILTERS_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return null;
		return {
			licTypes: parseMultiFilterValue(parsed.licTypes),
			licCats: parseMultiFilterValue(parsed.licCats),
			pclubs: parseMultiFilterValue(parsed.pclubs),
			hasPclubsKey: Object.prototype.hasOwnProperty.call(parsed, 'pclubs'),
			minManches: parsed.minManches
				? String(parsed.minManches)
				: (parsed.minParties ? String(parsed.minParties) : ''),
			q: parsed.q ? String(parsed.q) : '',
			psize: PLAYER_PAGE_SIZE_OPTIONS.includes(Number(parsed.psize))
				? Number(parsed.psize)
				: null,
		};
	} catch {
		return null;
	}
}

export function serializeMultiFilter(values) {
	if (!values?.length) return null;
	return values.join(MULTI_FILTER_SEPARATOR);
}
